/** Client-side EAD Script → Mermaid compiler (mirrors gateway ead_script_to_mermaid.py). */

const ARROW = /\s*(?:-->|—>)\s*/;
const ENTITY = /\[([^\]]+)\]/;
const SUB_ENTITY = /\.\[([^\]]+)\]/;
const CONDITION = /\{([^}]+)\}/;
const ACTION = /<([^>]+)>/;

type TokenKind = "entity" | "sub" | "condition" | "action";
type Token = [TokenKind, string];

const EAD_MERMAID_CLASS_DEFS = `
  classDef eadEntity fill:#fcd5b5,stroke:#d97706,stroke-width:2px,color:#111827,font-weight:600
  classDef eadSub fill:#fde68a,stroke:#f59e0b,stroke-width:2px,color:#111827
  classDef eadCondition fill:#ddd6fe,stroke:#6366f1,stroke-width:2px,color:#111827
  classDef eadAction fill:#0f766e,stroke:#14b8a6,stroke-width:1px,color:#f8fafc
`.trim();

function slug(label: string, index: number): string {
  const base = label.trim().replace(/[^a-zA-Z0-9]+/g, "_").replace(/^_|_$/g, "").toLowerCase();
  return `${base || "node"}_${index}`;
}

function tokenKey(kind: TokenKind, label: string): string {
  return `${kind}:${label.trim().toLowerCase()}`;
}

function extractTokens(segment: string): Token[] {
  const tokens: Token[] = [];
  let pos = 0;
  while (pos < segment.length) {
    const chunk = segment.slice(pos);
    let matched = false;
    for (const [kind, pattern] of [
      ["entity", ENTITY],
      ["sub", SUB_ENTITY],
      ["condition", CONDITION],
      ["action", ACTION],
    ] as const) {
      const m = pattern.exec(chunk);
      if (m && m.index === 0) {
        tokens.push([kind, m[1].trim()]);
        pos += m[0].length;
        matched = true;
        break;
      }
    }
    if (!matched) pos += 1;
  }
  return tokens;
}

function parseScriptLines(script: string): Token[][] {
  const lines = script.split("\n").map((ln) => ln.trim()).filter(Boolean);
  const parsed: Token[][] = [];
  for (const line of lines) {
    const tokens: Token[] = [];
    for (const segment of line.split(ARROW)) {
      tokens.push(...extractTokens(segment));
    }
    if (tokens.length) parsed.push(tokens);
  }
  return parsed;
}

function mermaidShape(kind: TokenKind, nodeId: string, label: string): string {
  const safe = label.replace(/"/g, "'");
  if (kind === "condition") return `${nodeId}{{"${safe}"}}`;
  if (kind === "action") return `${nodeId}["${safe}"]`;
  if (kind === "sub") return `${nodeId}[".${safe}"]`;
  return `${nodeId}["${safe}"]`;
}

class NodeRegistry {
  private index = 0;
  private byKey = new Map<string, string>();
  private meta = new Map<string, Token>();
  classesByKind: Record<TokenKind, string[]> = {
    entity: [],
    sub: [],
    condition: [],
    action: [],
  };

  idFor(kind: TokenKind, label: string): string {
    const key = tokenKey(kind, label);
    const existing = this.byKey.get(key);
    if (existing) return existing;
    const nodeId = slug(label, this.index++);
    this.byKey.set(key, nodeId);
    this.meta.set(nodeId, [kind, label]);
    this.classesByKind[kind].push(nodeId);
    return nodeId;
  }

  tokenIds(tokens: Token[]): string[] {
    return tokens.map(([kind, label]) => this.idFor(kind, label));
  }

  nodeDefsForIds(ids: string[], indent = "    "): string[] {
    const wanted = new Set(ids);
    return [...this.meta.entries()]
      .filter(([nodeId]) => wanted.has(nodeId))
      .map(([nodeId, [kind, label]]) => `${indent}${mermaidShape(kind, nodeId, label)}`);
  }
}

function branchGroups(parsed: Token[][]): Array<[number, number[]]> {
  const groups: Array<[number, number[]]> = [];
  let i = 0;
  while (i < parsed.length) {
    const startKey = parsed[i]?.[0] ? tokenKey(parsed[i][0][0], parsed[i][0][1]) : "";
    if (!startKey) {
      i += 1;
      continue;
    }
    const run = [i];
    let j = i + 1;
    while (j < parsed.length) {
      const other = parsed[j]?.[0] ? tokenKey(parsed[j][0][0], parsed[j][0][1]) : "";
      if (other === startKey) {
        run.push(j);
        j += 1;
      } else break;
    }
    if (run.length >= 2) {
      groups.push([i, run]);
      i = run[run.length - 1] + 1;
    } else i += 1;
  }
  return groups;
}

function branchMergeId(lineIndices: number[], parsed: Token[][], registry: NodeRegistry): string | null {
  const lastIds: string[] = [];
  for (const lineIdx of lineIndices) {
    const ids = registry.tokenIds(parsed[lineIdx]);
    if (ids.length > 1) lastIds.push(ids[ids.length - 1]);
  }
  if (lastIds.length >= 2 && new Set(lastIds).size === 1) return lastIds[0];
  return null;
}

function branchColumnTitle(tokens: Token[]): string {
  for (const [kind, label] of tokens.slice(1)) {
    if (kind === "action" || kind === "condition") return label.replace(/"/g, "'").slice(0, 56);
  }
  return "Path";
}

function tbChain(ids: string[], indent = "    "): string[] {
  if (!ids.length) return [];
  const out = [`${indent}${ids[0]}`];
  for (let idx = 0; idx < ids.length - 1; idx++) {
    out.push(`${indent}${ids[idx]} --> ${ids[idx + 1]}`);
  }
  return out;
}

function uniqueIds(ids: string[]): string[] {
  return [...new Set(ids)];
}

function lineTerminalId(lineIdx: number, parsed: Token[][], registry: NodeRegistry): string | null {
  const ids = registry.tokenIds(parsed[lineIdx] || []);
  return ids.length ? ids[ids.length - 1] : null;
}

export function compileEadScriptToMermaid(script: string): string {
  const parsed = parseScriptLines((script || "").trim());
  if (!parsed.length) return "";

  const registry = new NodeRegistry();
  for (const tokens of parsed) registry.tokenIds(tokens);

  const groups = branchGroups(parsed);
  const branchLineSet = new Set(groups.flatMap(([, run]) => run));

  const out: string[] = [
    "flowchart TB",
    "  %% EAD Script — stacked sections (entry, branches, tail)",
  ];

  const branchStart = groups[0]?.[0] ?? parsed.length;
  const entryLines = [...Array(branchStart).keys()].filter((idx) => !branchLineSet.has(idx));

  let sectionExit: string | null = null;

  if (entryLines.length) {
    const entryIds = uniqueIds(entryLines.flatMap((lineIdx) => registry.tokenIds(parsed[lineIdx])));
    out.push('  subgraph ead_entry[" "]', "    direction TB");
    out.push(...registry.nodeDefsForIds(entryIds, "    "));
    for (const lineIdx of entryLines) {
      out.push(...tbChain(registry.tokenIds(parsed[lineIdx]), "    "));
    }
    out.push("  end");
    sectionExit = lineTerminalId(entryLines[entryLines.length - 1], parsed, registry);
  }

  for (const [groupIdx, [, lineIndices]] of groups.entries()) {
    const anchorId = registry.tokenIds(parsed[lineIndices[0]])[0];
    const mergeId = branchMergeId(lineIndices, parsed, registry);
    const branchIds = new Set<string>([anchorId]);
    if (mergeId) branchIds.add(mergeId);

    const branchBody: string[] = [];
    let prevColExit: string | null = null;

    for (const [colIdx, lineIdx] of lineIndices.entries()) {
      let colIds = registry.tokenIds(parsed[lineIdx]).slice(1);
      if (mergeId && colIds.length && colIds[colIds.length - 1] === mergeId) {
        colIds = colIds.slice(0, -1);
      }
      if (!colIds.length) continue;
      colIds.forEach((id) => branchIds.add(id));

      const title = branchColumnTitle(parsed[lineIdx]);
      branchBody.push(`    subgraph ead_branch_${groupIdx}_col_${colIdx}["${title}"]`, "      direction TB");
      branchBody.push(...tbChain(colIds, "      "));
      branchBody.push("    end");

      if (colIdx === 0) {
        branchBody.push(`    ${anchorId} --> ${colIds[0]}`);
      } else if (prevColExit) {
        branchBody.push(`    ${prevColExit} --> ${colIds[0]}`);
      }
      prevColExit = colIds[colIds.length - 1];
    }

    if (mergeId && prevColExit && prevColExit !== mergeId) {
      branchBody.push(`    ${prevColExit} --> ${mergeId}`);
      prevColExit = mergeId;
    }

    if (sectionExit && sectionExit !== anchorId) {
      out.push(`  ${sectionExit} --> ${anchorId}`);
    }

    out.push(`  subgraph ead_branch_${groupIdx}[" "]`, "    direction TB");
    out.push(...registry.nodeDefsForIds([...branchIds], "    "));
    out.push(...branchBody);
    out.push("  end");

    sectionExit = mergeId || prevColExit || anchorId;
  }

  const tailStart = groups.length ? groups[groups.length - 1][1][groups[groups.length - 1][1].length - 1] + 1 : 0;
  const tailLines = [...Array(parsed.length - tailStart).keys()]
    .map((n) => tailStart + n)
    .filter((idx) => !branchLineSet.has(idx));

  for (const lineIdx of tailLines) {
    const ids = registry.tokenIds(parsed[lineIdx]);
    if (ids.length <= 1) continue;
    const tailEntry = ids[0];
    if (sectionExit && sectionExit !== tailEntry) {
      out.push(`  ${sectionExit} --> ${tailEntry}`);
    }
    out.push(`  subgraph ead_tail_${lineIdx}[" "]`, "    direction TB");
    out.push(...registry.nodeDefsForIds(ids, "    "));
    out.push(...tbChain(ids, "    "));
    out.push("  end");
    sectionExit = ids[ids.length - 1];
  }

  out.push(EAD_MERMAID_CLASS_DEFS);
  for (const [kind, className] of [
    ["entity", "eadEntity"],
    ["sub", "eadSub"],
    ["condition", "eadCondition"],
    ["action", "eadAction"],
  ] as const) {
    const ids = registry.classesByKind[kind];
    if (ids.length) out.push(`  class ${ids.join(",")} ${className}`);
  }

  return `${out.join("\n")}\n`;
}

export function effectiveMermaidForSkill(
  skill: { configJson?: Record<string, unknown> } | null,
  scriptOverride?: string,
): string {
  const cfg = skill?.configJson;
  const savedScript =
    typeof cfg?.eadScript === "string"
      ? cfg.eadScript
      : typeof cfg?.ead_script === "string"
        ? cfg.ead_script
        : "";

  const script = (scriptOverride ?? savedScript).trim();
  const savedTrimmed = savedScript.trim();

  if (scriptOverride !== undefined && script && script !== savedTrimmed) {
    const compiled = compileEadScriptToMermaid(script);
    if (compiled) return compiled;
  }

  const stored = cfg?.eadMermaidMap ?? cfg?.ead_mermaid_map;
  if (typeof stored === "string" && stored.trim()) return stored.trim();

  return compileEadScriptToMermaid(script);
}
